// Modules
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';

import Person from 'services/data/person';


const PANEL_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 360px',
  minWidth: 320,
  maxWidth: 420,
  height: 'calc(100vh - 220px)',
  background: '#ffffff',
  border: '1px solid #e3e6ea',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(20, 30, 45, 0.04)',
  overflow: 'hidden'
};

const SECTION_TITLE_STYLE = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#6b7785',
  margin: '0 0 10px'
};

const AVATAR_COLORS = ['#4a90e2', '#8e44ad', '#16a085', '#e67e22', '#c0392b', '#2c3e50', '#d35400'];

function avatarColor(person) {
  const teamColor = person?.team?.color;
  if (teamColor) 
    return teamColor;

  const key = person && (person.id || person.email || Person.fullName(person)) || '';
  const str = String(key);
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function Avatar({ person, size = 32 }) {
  const initials = person ? Person.getInitials(person) : '';
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: '50%',
        background: avatarColor(person),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 600,
        letterSpacing: '0.02em'
      }}
    >
      {initials || '?'}
    </div>
  );
}

function formatDuration(ms) {
  if (!ms || ms < 1000) return '<1s';
  const d = moment.duration(ms);
  if (d.asMinutes() < 1) return `${Math.round(d.asSeconds())}s`;
  if (d.asHours() < 1) return `${Math.round(d.asMinutes())}m`;
  if (d.asDays() < 1) {
    const h = Math.floor(d.asHours());
    const m = Math.round(d.asMinutes()) - h * 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.floor(d.asDays())}d ${Math.floor(d.asHours()) % 24}h`;
}

function CurrentEntry({ entry, now }) {
  const connectedAt = moment(entry.connectedAt);
  const duration = formatDuration(now - connectedAt.valueOf());

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: 'linear-gradient(135deg, #e8f7ee 0%, #f3fbf6 100%)',
        border: '1px solid #b9e5c7',
        borderRadius: 6,
        marginBottom: 8
      }}
    >
      <span className="mfa-pulse-dot" style={{ background: '#2ecc71' }} />
      <Avatar person={entry.person} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#1f2d3d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {Person.fullName(entry.person) || 'Unknown user'}
        </div>
        <div style={{ fontSize: 12, color: '#52606d' }}>
          Connected · {duration}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#7b8794', whiteSpace: 'nowrap' }}>
        {connectedAt.format('h:mm A')}
      </div>
    </div>
  );
}

function HistoryEntry({ entry, isLast }) {
  const connectedAt = moment(entry.connectedAt);
  const disconnectedAt = entry.disconnectedAt ? moment(entry.disconnectedAt) : null;
  const duration = disconnectedAt ? formatDuration(disconnectedAt.valueOf() - connectedAt.valueOf()) : null;
  const sameDay = disconnectedAt && connectedAt.isSame(disconnectedAt, 'day');

  return (
    <div style={{ position: 'relative', display: 'flex', gap: 12, paddingBottom: isLast ? 0 : 14 }}>
      <div style={{ position: 'relative', width: 10, flex: '0 0 10px' }}>
        <span
          style={{
            position: 'absolute', top: 14, left: 3, width: 8, height: 8,
            borderRadius: '50%', background: '#b0bac5', border: '2px solid #fff', boxShadow: '0 0 0 1px #d5dae0'
          }}
        />
        { !isLast &&
          <span style={{ position: 'absolute', top: 22, bottom: -6, left: 6, width: 1, background: '#e3e6ea' }} />
        }
      </div>
      <div style={{ display: 'flex', gap: 10, flex: 1, minWidth: 0 }}>
        <Avatar person={entry.person} size={28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontWeight: 600, color: '#1f2d3d', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {Person.fullName(entry.person) || 'Unknown user'}
            </span>
            { duration &&
              <span style={{ fontSize: 11, color: '#7b8794', whiteSpace: 'nowrap' }}>{duration}</span>
            }
          </div>
          <div style={{ fontSize: 12, color: '#6b7785', marginTop: 2 }}>
            {connectedAt.format('MMM D, h:mm A')}
            { disconnectedAt && ` – ${disconnectedAt.format(sameDay ? 'h:mm A' : 'MMM D, h:mm A')}` }
          </div>
        </div>
      </div>
    </div>
  );
}

function MfaConnectionLog({ entries = [], isLoading, style }) {
  const [now, setNow] = useState(() => Date.now());

  const active = _.filter(entries, (e) => !e.disconnectedAt);
  const history = _.filter(entries, (e) => e.disconnectedAt);

  useEffect(() => {
    if (!active.length) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active.length]);

  return (
    <div style={{ ...PANEL_STYLE, ...style }}>
      <style>{`
        @keyframes mfa-pulse { 0% { box-shadow: 0 0 0 0 rgba(46,204,113,0.55); } 70% { box-shadow: 0 0 0 8px rgba(46,204,113,0); } 100% { box-shadow: 0 0 0 0 rgba(46,204,113,0); } }
        .mfa-pulse-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; animation: mfa-pulse 1.6s infinite; flex: 0 0 10px; }
      `}</style>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #eef0f3', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1f2d3d' }}>Connection Log</h5>
        <span style={{ fontSize: 11, color: '#7b8794' }}>
          {active.length} active {active.length === 1 ? 'connection' : 'connections'}
        </span>
      </div>
      <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
        <div style={{ marginBottom: 18 }}>
          <h6 style={SECTION_TITLE_STYLE}>Currently Connected</h6>
          { active.length
            ? _.map(active, (entry) => <CurrentEntry key={entry.id} entry={entry} now={now} />)
            : <div style={{ fontSize: 12, color: '#97a1ad', fontStyle: 'italic', padding: '4px 2px' }}>No one is connected.</div>
          }
        </div>
        <div>
          <h6 style={SECTION_TITLE_STYLE}>History</h6>
          { isLoading && !entries.length &&
            <div style={{ fontSize: 12, color: '#97a1ad', padding: '4px 2px' }}>Loading…</div>
          }
          { !isLoading && !history.length &&
            <div style={{ fontSize: 12, color: '#97a1ad', fontStyle: 'italic', padding: '4px 2px' }}>No previous connections.</div>
          }
          { !!history.length &&
            <div>
              {_.map(history, (entry, idx) => (
                <HistoryEntry key={entry.id} entry={entry} isLast={idx === history.length - 1} />
              ))}
            </div>
          }
        </div>
      </div>
    </div>
  );
}


// Exports
export default MfaConnectionLog;
